
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Mail, Phone, MapPin } from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';
import EditAuthorDialog from './EditAuthorDialog';
import AuthorActions from './AuthorActions';

interface AuthorProfileProps {
  author: any;
}

const AuthorProfile: React.FC<AuthorProfileProps> = ({ author }) => {
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Profile</CardTitle>
          <EditAuthorDialog author={author} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center text-center mb-6">
          <Avatar className="h-24 w-24 mb-4">
            <AvatarImage src={author.avatar_url} />
            <AvatarFallback className="bg-royalty-600 text-white text-xl">
              {getInitials(author.name)}
            </AvatarFallback>
          </Avatar>
          <h3 className="text-xl font-semibold">{author.name}</h3>
          <div className="flex items-center mt-1 text-muted-foreground">
            <Mail className="h-4 w-4 mr-1" />
            {author.email}
          </div>
          {author.phone && (
            <div className="flex items-center mt-1 text-muted-foreground">
              <Phone className="h-4 w-4 mr-1" />
              {author.phone}
            </div>
          )}
          {author.address && (
            <div className="flex items-center mt-1 text-muted-foreground">
              <MapPin className="h-4 w-4 mr-1" />
              <span className="text-sm">{author.address}</span>
            </div>
          )}
          <Badge variant="outline" className="mt-3 capitalize">
            {author.role}
          </Badge>
        </div>

        <Separator className="my-6" />
        
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-1">Current Balance</h4>
            <p className="text-2xl font-bold">{formatCurrency(author.balance || 0)}</p>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-1">Books Published</h4>
            <p className="text-2xl font-bold">{author.books?.length || 0}</p>
          </div>
          
          <AuthorActions authorId={author.id} />
        </div>
      </CardContent>
    </Card>
  );
};

export default AuthorProfile;
